import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconBack, IconInfo } from '../components/Icon';
import { CODE_LENGTH } from '../lib/auth-config';
import {
  CodeError, clearPendingCodeRequest, getPendingCodeRequest, requestCode, verifyCode,
} from '../lib/session';

/**
 * Artboard 1b — the email path (D46).
 * Two steps on one screen. A guest is standing at check-in with luggage while
 * this happens, so every second of it is felt: the keypad comes up on its own,
 * a pasted or autofilled code submits itself, and changing a mistyped address
 * is one tap rather than a page load.
 */

const RESEND_SECONDS = 30;
const SPAM_HINT_AFTER_MS = 20_000;

export default function EmailCode() {
  const navigate = useNavigate();
  const pending = getPendingCodeRequest();
  const [step, setStep] = useState<'email' | 'code'>(pending ? 'code' : 'email');
  const [email, setEmail] = useState(pending?.email ?? '');
  const [sentAt, setSentAt] = useState<number | null>(pending?.sentAt ?? null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const codeInput = useRef<HTMLInputElement>(null);

  // One timer drives the cooldown, the spam hint and nothing else.
  useEffect(() => {
    if (step !== 'code') return;
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [step]);

  useEffect(() => {
    if (step === 'code') codeInput.current?.focus();
  }, [step]);

  const secondsLeft = useMemo(() => {
    if (!sentAt) return 0;
    void tick;
    return Math.max(0, RESEND_SECONDS - Math.floor((Date.now() - sentAt) / 1000));
  }, [sentAt, tick]);

  const showSpamHint = Boolean(sentAt) && Date.now() - (sentAt ?? 0) > SPAM_HINT_AFTER_MS;

  async function send(address: string) {
    setBusy(true);
    setError(null);
    try {
      const req = await requestCode(address);
      setSentAt(req.sentAt);
      setStep('code');
      setCode('');
    } catch {
      setError('That did not send — the connection dropped. Try again in a moment.');
    } finally {
      setBusy(false);
    }
  }

  async function submit(value: string) {
    setBusy(true);
    setError(null);
    try {
      await verifyCode(value);
      navigate('/auth/callback', { replace: true });
    } catch (err) {
      const kind = err instanceof CodeError ? err.kind : 'invalid';
      setCode('');
      codeInput.current?.focus();
      // A spent challenge cannot be retried, so drop the cooldown with it —
      // otherwise the only action that can still work sits behind a countdown,
      // which is a cruel thing to do to someone at a check-in desk.
      if (kind === 'expired') setSentAt(null);
      setError(
        kind === 'expired'
          ? 'That code has been used up. Send yourself a new one — it only takes a second.'
          : kind === 'offline'
            ? 'No connection. Nothing is lost; try again when a bar or two comes back.'
            : kind === 'rate_limited'
              ? 'Too many tries from this connection. Give it a few minutes, or come find one of us at the desk.'
              : "That code isn't right. Have another look at the email — the newest one is the one that works.",
      );
      setBusy(false);
    }
  }

  function onCodeChange(raw: string) {
    const digits = raw.replace(/\D/g, '').slice(0, CODE_LENGTH);
    setCode(digits);
    setError(null);
    // Auto-submit. Do not make them find a button.
    if (digits.length === CODE_LENGTH) void submit(digits);
  }

  if (step === 'email') {
    return (
      <div className="auth">
        <div className="auth-col">
          <button type="button" className="btn btn-secondary" style={{ alignSelf: 'flex-start', minHeight: 44, gap: 8 }} onClick={() => navigate('/')}>
            <IconBack size={16} /> Back
          </button>

          <h2 style={{ margin: '28px 0 6px' }}>What's your email?</h2>
          <p style={{ fontSize: 14, opacity: 0.75, margin: 0 }}>
            We'll send you eight digits. No password, and nothing to set up.
          </p>

          <form
            onSubmit={(e) => { e.preventDefault(); if (email.includes('@')) void send(email); }}
            style={{ marginTop: 26 }}
          >
            <div className="field">
              <label htmlFor="email">Email address</label>
              <input
                id="email" className="input" type="email" inputMode="email"
                autoComplete="email" autoCapitalize="off" autoCorrect="off" spellCheck={false}
                autoFocus style={{ minHeight: 50, fontSize: 16 }}
                value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
              />
            </div>
            {error && <p style={{ fontSize: 13, color: 'var(--color-accent-700)', margin: '14px 0 0' }} role="alert">{error}</p>}
            <button type="submit" className="btn btn-primary btn-block" style={{ minHeight: 54, fontSize: 16, marginTop: 24 }} disabled={busy || !email.includes('@')}>
              {busy ? 'Sending…' : 'Send me a code'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="auth">
      <div className="auth-col">
        <button
          type="button" className="btn btn-secondary" style={{ alignSelf: 'flex-start', minHeight: 44, gap: 8 }}
          onClick={() => { clearPendingCodeRequest(); setStep('email'); setError(null); }}
        >
          <IconBack size={16} /> Change address
        </button>

        <h2 style={{ margin: '28px 0 6px' }}>Check your email</h2>
        <p style={{ fontSize: 14, opacity: 0.75, margin: 0 }}>
          Eight digits, sent to <strong>{email}</strong>. It expires in ten minutes.
        </p>

        <div className={`code-row${error ? ' bad' : ''}`}>
          {Array.from({ length: CODE_LENGTH }, (_, i) => {
            const char = code[i];
            const isCursor = i === code.length;
            const cls = ['code-cell', char ? 'filled' : isCursor ? 'on' : 'blank'].join(' ');
            return (
              <div key={i} className={cls} aria-hidden="true">
                {char ?? (isCursor ? <span className="caret" /> : null)}
              </div>
            );
          })}
          <input
            ref={codeInput} className="code-input" value={code}
            onChange={(e) => onCodeChange(e.target.value)}
            type="text" inputMode="numeric" pattern="\d*" autoComplete="one-time-code"
            maxLength={CODE_LENGTH} aria-label={`${CODE_LENGTH}-digit code`} disabled={busy}
          />
        </div>

        {error && <p style={{ fontSize: 13, color: 'var(--color-accent-700)', margin: '20px 0 0' }} role="alert">{error}</p>}

        {showSpamHint && (
          <div className="card hint">
            <div className="hint-title"><IconInfo size={17} /> Nothing yet?</div>
            <p>Have a look in your spam folder — it lands there sometimes. Or come find one of us at the desk and we'll sign you in.</p>
          </div>
        )}

        {/* `marginTop: auto` pushes this to the foot on a tall screen but
            guarantees nothing on a short one, where it ended up crowding the
            content above. paddingTop is the floor under that. */}
        <div style={{
          marginTop: 'auto', paddingTop: 32, paddingBottom: 24,
          display: 'flex', flexDirection: 'column', gap: 14, alignItems: 'center',
        }}>
          {secondsLeft > 0 ? (
            <span className="tag tag-neutral" style={{ padding: '7px 16px' }}>
              Resend code in 0:{String(secondsLeft).padStart(2, '0')}
            </span>
          ) : (
            <button type="button" className="btn btn-ghost" onClick={() => void send(email)} disabled={busy}>
              Send another code
            </button>
          )}
          <span style={{ fontSize: 11, opacity: 0.5, textAlign: 'center' }}>The keypad is up; the code pastes in one tap.</span>
        </div>
      </div>
    </div>
  );
}
